import React, { useState } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { getUrlToForgotPasswordPage, getUrlToLoginCallbackPage } from "pages/urls";
import { ActionButton } from "components/ActionButton";
import { TextInput } from "components/TextInput";
import { PasswordInput } from "components/PasswordInput";
import { login } from "api";
import { HttpApiResponseError } from "shared/http-api";
import { saveUserToken, isLoggedIn } from "api/auth-storage";
import { Link } from "react-router-dom";

const LOGIN_CALLBACK_PAGE_URL = getUrlToLoginCallbackPage();
const FORGOT_PASSWORD_PAGE_URL = getUrlToForgotPasswordPage();

export const LoginForm = ({
  emailInputRef
}: {
  emailInputRef?: React.MutableRefObject<HTMLInputElement | null>
}) => {
  const navigate = useNavigate();

  const isAuthenticated = isLoggedIn();
  const [urlSearchParams] = useSearchParams();

  const forgotPasswordPageUrl = FORGOT_PASSWORD_PAGE_URL + '?' + urlSearchParams.toString();
  const loginCallbackPageUrl = LOGIN_CALLBACK_PAGE_URL + '?' + urlSearchParams.toString();

  const [formData, setFormData] = useState({
    identifier: "",
    password: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string,string>>({});

  const onChange = (e) => {
    setErrors({});
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };
  
  const checkErrors = () => {
    const errors: Record<string,string> = {};
    if (!formData.identifier) errors.identifier = 'Email or Phone Number is required';
    if (!formData.password) errors.password = 'Password is required';
    setErrors(errors);
    return Object.keys(errors).length > 0;
  }

  const canSubmit = !submitting && Object.keys(errors).length === 0

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!canSubmit) return;
    if (checkErrors()) return;
    setSubmitting(true);
    const { identifier, password } = formData;
    try {
      const json = await login({ identifier, password });
      saveUserToken(json);
      navigate(loginCallbackPageUrl);
    } catch (err) {
      if (!(err instanceof HttpApiResponseError)) {
        setErrors({ name: 'Could not login because of an unknown error' });
      } else {
        const response = await err.json();
        if (!response.errors || response.errors.length === 0) {
          setErrors({ name: 'Could not login because of an unknown error' });
        } else {
          setErrors(Object.fromEntries(response.errors.map(err => [err.path, err.message])));
        }
      }
    }
    setSubmitting(false);
  };

  if (isAuthenticated) {
    return <Navigate to={loginCallbackPageUrl} />;
  }

  return (
    <form className="" noValidate onSubmit={(e) => onSubmit(e)}>
      <TextInput
        label="Email / Phone Number"
        name="identifier"
        type="identifier"
        value={formData.identifier}
        onChange={(val, e) => onChange(e)}
        error={errors.identifier}
        disabled={submitting}
        placeholder="Enter your email or phone number"
        required
        ref={emailInputRef}
      />
      <PasswordInput
        label="Password"
        name="password"
        value={formData.password}
        onChange={(val, e) => onChange(e)}
        placeholder="Enter your password"
        required
        disabled={submitting}
        error={errors.password}
      />
      <div className="text-right -mt-4">
        <Link to={forgotPasswordPageUrl} className="text-md text-violet-600">
          Forgot Password?
        </Link>
      </div>
      <div className="mt-4">
        <ActionButton
          type='submit'
          label='Log In'
          workingLabel='Logging In'
          disabled={!canSubmit}
          working={submitting}
        />
      </div>
    </form>
  );
};
